/**
 * Piani di cura — Kanban admin sync GHL.
 *
 * Layout: header (selettore pipeline + azioni) + kanban board con
 * colonne=stages e card=opportunità. Supporta drag&drop native HTML5
 * per spostare opp tra gli stage.
 *
 * Route: /piani-cura, protetta da permesso `piani-cura`.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Plus, Pencil, Trash2, Archive, ArchiveRestore, X, RefreshCw,
  ChevronDown, AlertTriangle, FolderOpen, GripVertical, Save, Palette,
  Phone, Mail, Calendar, User, Euro, Clock, Search, Stethoscope, UserCog,
  FileText, Camera, Eye, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";
import { Toaster, toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getStagePermission } from "@/utils/stagePermissions";

// Deve rispecchiare `PIPELINE_TO_MODE` in `OpportunitaCreatePage.jsx`.
// Mappa il pipeline_id GHL al reparto per costruire la URL di edit del
// piano di cura originale (/piano-cura-clinico o /piano-cura-ortodonzia).
const PIPELINE_TO_MODE = {
  "529iRmd3Wh7bjdgfHpP9": "clinico",
  "Eu6Xz0GLvHfN5PejJ4SV": "ortodonzia",
  "I2wcBQKe3y6DhTYfFySx": "clinico",
  "l191W5Qj1vs4QSEc9xVd": "ortodonzia",
  "he2K2EvoAaa3SxaikphM": "clinico",
  "YNar5ZkxXDvFp31UMmHY": "ortodonzia",
  "tmyDM7u0ygOPtn9rYMQT": "clinico",
  "cVQGQNimL45cGp3OBsAD": "clinico",
  "MyxgABpCKYCkMRBoQMDc": "ortodonzia",
  "T2q5AK85MGrZBv1z0nQy": "ortodonzia",
};

const API = window.location.origin + "/api";
const STATUS_COLORS = {
  open: "bg-blue-100 text-blue-700",
  won: "bg-emerald-100 text-emerald-700",
  lost: "bg-rose-100 text-rose-700",
  abandoned: "bg-slate-100 text-slate-600",
};

export default function PianiCuraPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [pipelines, setPipelines] = useState([]);
  const [selectedPid, setSelectedPid] = useState(null);
  const [oppsByStage, setOppsByStage] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingOpps, setLoadingOpps] = useState(false);
  const [pipelineDialog, setPipelineDialog] = useState({ open: false, mode: null, pipeline: null });
  const [stageDialog, setStageDialog] = useState({ open: false, mode: null, pipelineId: null, stage: null });
  const [oppDialog, setOppDialog] = useState({ open: false, opp: null });
  const [detailDialog, setDetailDialog] = useState({ open: false, oppId: null });
  const [archiveDialog, setArchiveDialog] = useState({ open: false });
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [assicFilter, setAssicFilter] = useState(""); // "" = tutte

  const handleSetStageColor = async (stage, color) => {
    try {
      await axios.put(
        `${API}/piani-cura/pipelines/${selectedPid}/stages/${stage.id}/color`,
        { color },
        { withCredentials: true }
      );
      toast.success(color ? "Colore aggiornato" : "Colore rimosso");
      fetchPipelines();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento colore");
    }
  };

  // Fetch pipelines
  const fetchPipelines = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/piani-cura/pipelines`, { withCredentials: true });
      const list = res.data.pipelines || [];
      setPipelines(list);
      // Auto-seleziona la prima pipeline se non c'è già selezione
      if (list.length && !selectedPid) setSelectedPid(list[0].id);
    } catch (err) {
      console.error("fetchPipelines:", err);
      toast.error(err.response?.data?.detail || "Errore caricamento pipeline");
    } finally {
      setLoading(false);
    }
  }, [selectedPid]);

  // Fetch opps della pipeline selezionata
  const fetchOpps = useCallback(async (pid) => {
    if (!pid) return;
    try {
      setLoadingOpps(true);
      const res = await axios.get(
        `${API}/piani-cura/pipelines/${pid}/opportunities`,
        { withCredentials: true }
      );
      setOppsByStage(res.data.opportunities_by_stage || {});
    } catch (err) {
      console.error("fetchOpps:", err);
      toast.error("Errore caricamento opportunità");
    } finally {
      setLoadingOpps(false);
    }
  }, []);

  useEffect(() => { fetchPipelines(); }, [fetchPipelines]);
  useEffect(() => { if (selectedPid) fetchOpps(selectedPid); }, [selectedPid, fetchOpps]);

  // Sync on-demand da GHL: aggiorna `opp_stage_history` (tracked) e
  // invalida la cache untracked, poi rilegge le opp della pipeline
  // corrente. Utile quando l'utente vuole vedere subito modifiche fatte
  // in GHL senza attendere il cron di 5 min.
  const handleSyncFromGhl = useCallback(async () => {
    try {
      setSyncing(true);
      const res = await axios.post(
        `${API}/piani-cura/sync-from-ghl`, {},
        { withCredentials: true, timeout: 120000 },
      );
      if (res.data?.status === "already_running") {
        toast.info("Un sync è già in corso, attendi qualche secondo");
      } else {
        const s = res.data?.tracked_summary || {};
        const changes = (s.new || 0) + (s.transitions || 0);
        toast.success(
          changes > 0
            ? `Sync completato: ${changes} modifiche (${s.transitions || 0} spostamenti, ${s.new || 0} nuove)`
            : "Sync completato: nessuna novità"
        );
      }
      // Rilegge la pipeline corrente + refresh conteggi
      await Promise.all([
        selectedPid ? fetchOpps(selectedPid) : Promise.resolve(),
        fetchPipelines(),
      ]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore sync da GHL");
    } finally {
      setSyncing(false);
    }
  }, [selectedPid, fetchOpps, fetchPipelines]);

  const currentPipeline = pipelines.find((p) => p.id === selectedPid);

  // Reset filtro assicurazione quando cambio pipeline (per evitare
  // filtri residui non presenti nella nuova pipeline).
  useEffect(() => { setAssicFilter(""); }, [selectedPid]);

  // Lista univoca delle assicurazioni presenti nella pipeline corrente
  // (ordinate alfabeticamente, escludendo vuoti).
  const availableAssicurazioni = useMemo(() => {
    const set = new Set();
    Object.values(oppsByStage).forEach((opps) => {
      opps.forEach((o) => {
        if (o.source && o.source.trim()) set.add(o.source.trim());
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "it"));
  }, [oppsByStage]);

  // Filtro paziente + assicurazione sulle card visualizzate.
  // Confronto case-insensitive su contactName e oppName. L'assicurazione
  // è confrontata esatta (dopo l'enrichment del backend).
  const filteredOppsByStage = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const af = assicFilter.trim();
    if (!q && !af) return oppsByStage;
    const out = {};
    Object.entries(oppsByStage).forEach(([sid, opps]) => {
      out[sid] = opps.filter((o) => {
        if (af && (o.source || "").trim() !== af) return false;
        if (q) {
          const name = (o.contactName || o.oppName || "").toLowerCase();
          if (!name.includes(q)) return false;
        }
        return true;
      });
    });
    return out;
  }, [oppsByStage, searchQuery, assicFilter]);

  // ── Drag & drop handlers ───────────────────
  const dragging = useRef(null);

  const handleDragStart = (opp, e) => {
    dragging.current = opp;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", opp.id);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const handleDrop = async (targetStageId, e) => {
    e.preventDefault();
    const opp = dragging.current;
    dragging.current = null;
    if (!opp || opp.stageId === targetStageId) return;

    // Optimistic UI: sposto localmente prima della conferma
    setOppsByStage((prev) => {
      const next = { ...prev };
      next[opp.stageId] = (next[opp.stageId] || []).filter((o) => o.id !== opp.id);
      next[targetStageId] = [{ ...opp, stageId: targetStageId }, ...(next[targetStageId] || [])];
      return next;
    });

    try {
      await axios.put(
        `${API}/piani-cura/opportunities/${opp.id}/move`,
        { pipelineStageId: targetStageId },
        { withCredentials: true }
      );
      toast.success("Opportunità spostata");
      // Aggiorna conteggi pipeline
      fetchPipelines();
    } catch (err) {
      console.error("move opp:", err);
      toast.error("Errore spostamento — ripristino");
      // Rollback: rifetch
      fetchOpps(selectedPid);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <PageHeader title="Pipeline" subtitle="Gestione pipeline, fasi e piani di cura sincronizzati con GHL" />
      <Toaster position="top-right" />

      <div className="max-w-full mx-auto px-4 sm:px-6 pt-6" data-testid="piani-cura-content">
        {/* Toolbar: pipeline selector + search + actions */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex-1 min-w-[240px] flex flex-wrap gap-3 items-center">
            <div className="relative">
              <select
                value={selectedPid || ""}
                onChange={(e) => setSelectedPid(e.target.value)}
                data-testid="pipeline-selector"
                className="w-full sm:w-auto min-w-[280px] h-11 pl-4 pr-10 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-800 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400"
              >
                {pipelines.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.opps_total})</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            {/* Cerca paziente */}
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca paziente…"
                data-testid="search-patient-input"
                className="w-full h-11 pl-9 pr-9 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                  data-testid="search-patient-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {/* Filtro Assicurazione */}
            <div className="relative min-w-[220px]">
              <select
                value={assicFilter}
                onChange={(e) => setAssicFilter(e.target.value)}
                data-testid="filter-assicurazione"
                className="w-full h-11 pl-4 pr-9 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400"
              >
                <option value="">Tutte le assicurazioni</option>
                {availableAssicurazioni.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              {assicFilter && (
                <button
                  type="button"
                  onClick={() => setAssicFilter("")}
                  className="absolute right-8 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                  data-testid="filter-assicurazione-clear"
                  title="Rimuovi filtro"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchOpps(selectedPid)}
              disabled={loadingOpps}
              data-testid="refresh-opps-btn"
            >
              {loadingOpps ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncFromGhl}
              disabled={syncing}
              data-testid="sync-from-ghl-btn"
              title="Scarica ora le opportunità aggiornate da GoHighLevel"
            >
              {syncing ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sync GHL…</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-1.5" /> Sync GHL</>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setArchiveDialog({ open: true })}
              data-testid="open-archive-btn"
            >
              <Archive className="w-4 h-4 mr-1.5" /> Archivio
            </Button>
            {isAdmin && currentPipeline && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPipelineDialog({ open: true, mode: "edit", pipeline: currentPipeline })}
                data-testid="edit-pipeline-btn"
              >
                <Pencil className="w-4 h-4 mr-1.5" /> Rinomina
              </Button>
            )}
            {isAdmin && currentPipeline && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStageDialog({ open: true, mode: "create", pipelineId: currentPipeline.id })}
                data-testid="add-stage-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Nuova fase
              </Button>
            )}
            {isAdmin && (
              <Button
                size="sm"
                onClick={() => setPipelineDialog({ open: true, mode: "create" })}
                data-testid="new-pipeline-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Nuova pipeline
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400" data-testid="piani-cura-loading">
            <Loader2 className="w-8 h-8 mx-auto animate-spin" />
            <p className="mt-3 text-sm">Caricamento pipeline…</p>
          </div>
        ) : !currentPipeline ? (
          <div className="text-center py-16 text-slate-400">
            <FolderOpen className="w-12 h-12 mx-auto opacity-40" />
            <p className="mt-3">Nessuna pipeline trovata su GHL</p>
          </div>
        ) : (
          <KanbanBoard
            pipeline={currentPipeline}
            oppsByStage={filteredOppsByStage}
            loading={loadingOpps}
            isAdmin={isAdmin}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onEditStage={(s) => setStageDialog({ open: true, mode: "edit", pipelineId: currentPipeline.id, stage: s })}
            onDeleteStage={(s) => setStageDialog({ open: true, mode: "delete", pipelineId: currentPipeline.id, stage: s })}
            onOppClick={(o) => setDetailDialog({ open: true, oppId: o.id })}
            onSetStageColor={handleSetStageColor}
          />
        )}
      </div>

      <PipelineDialog
        state={pipelineDialog}
        onClose={() => setPipelineDialog({ open: false })}
        onSuccess={() => { setPipelineDialog({ open: false }); fetchPipelines(); }}
      />
      <StageDialog
        state={stageDialog}
        onClose={() => setStageDialog({ open: false })}
        onSuccess={() => { setStageDialog({ open: false }); fetchPipelines(); }}
      />
      <OppDialog
        state={oppDialog}
        onClose={() => setOppDialog({ open: false })}
        onSuccess={() => { setOppDialog({ open: false }); fetchOpps(selectedPid); fetchPipelines(); }}
      />
      <OppDetailDialog
        state={detailDialog}
        user={user}
        onClose={() => setDetailDialog({ open: false, oppId: null })}
      />
      <ArchiveDialog
        state={archiveDialog}
        onClose={() => setArchiveDialog({ open: false })}
        onRestored={() => { fetchOpps(selectedPid); fetchPipelines(); }}
      />
    </div>
  );
}


// ─── Kanban board ────────────────────────────────

// Palette default (10 colori pastello per intestazione stage)
const STAGE_COLOR_PRESETS = [
  "", // default (nessun colore = grigio)
  "#fee2e2", // rosso chiaro
  "#fed7aa", // arancio chiaro
  "#fef3c7", // giallo chiaro
  "#d1fae5", // verde chiaro
  "#cffafe", // ciano chiaro
  "#dbeafe", // azzurro chiaro
  "#ede9fe", // viola chiaro
  "#fce7f3", // rosa chiaro
  "#e5e7eb", // grigio chiaro
];

function fmtEuro(n) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return "—"; }
}

function KanbanBoard({ pipeline, oppsByStage, loading, isAdmin, onDragStart, onDragOver, onDrop, onEditStage, onDeleteStage, onOppClick, onSetStageColor }) {
  return (
    // Altezza fissa = viewport meno l'offset dell'header/toolbar (~230px).
    // Così lo scroll verticale avviene DENTRO ogni colonna (cards)
    // mentre l'intestazione dello stage resta ancorata in cima.
    <div
      className="overflow-x-auto overflow-y-hidden scrollbar-hide"
      style={{ height: "calc(100vh - 230px)" }}
      data-testid="kanban-board"
    >
      <div className="flex gap-2 h-full min-w-full">
        {pipeline.stages.map((stage) => {
          const opps = oppsByStage[stage.id] || [];
          return (
            <StageColumn
              key={stage.id}
              stage={stage}
              opps={opps}
              loading={loading}
              isAdmin={isAdmin}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onEdit={() => onEditStage(stage)}
              onDelete={() => onDeleteStage(stage)}
              onSetColor={(color) => onSetStageColor(stage, color)}
              onDragStart={onDragStart}
              onOppClick={onOppClick}
            />
          );
        })}
      </div>
    </div>
  );
}

function StageColumn({ stage, opps, loading, isAdmin, onDragOver, onDrop, onEdit, onDelete, onSetColor, onDragStart, onOppClick }) {
  const [showPalette, setShowPalette] = useState(false);
  const totalValue = opps.reduce((sum, o) => sum + (o.monetaryValue || 0), 0);
  const headerColor = stage.color || "#e2e8f0"; // slate-200 default

  return (
    <div
      className="flex-shrink-0 w-[380px] flex flex-col h-full min-h-0"
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(stage.id, e)}
      data-testid={`kanban-column-${stage.id}`}
    >
      <div
        className="rounded-xl px-3 py-2.5 mb-3 flex-shrink-0"
        style={{ backgroundColor: headerColor }}
      >
        <div className="flex items-center justify-between gap-1.5 mb-1">
          <p className="text-xs font-bold text-slate-900 uppercase tracking-wide truncate flex-1">{stage.name}</p>
          {isAdmin && (
            <div className="relative flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowPalette((v) => !v)}
                className="p-1 rounded hover:bg-white/60 text-slate-600 hover:text-slate-800 transition-colors"
                title="Cambia colore"
                data-testid={`color-stage-${stage.id}`}
              >
                <Palette className="w-3.5 h-3.5" />
              </button>
              {showPalette && (
                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 p-2 z-10" data-testid={`color-palette-${stage.id}`}>
                  <div className="grid grid-cols-5 gap-1.5">
                    {STAGE_COLOR_PRESETS.map((c) => (
                      <button
                        key={c || "none"}
                        type="button"
                        onClick={() => { onSetColor(c); setShowPalette(false); }}
                        className="w-6 h-6 rounded-md border border-slate-300 hover:scale-110 transition-transform"
                        style={{ backgroundColor: c || "transparent", backgroundImage: !c ? "linear-gradient(45deg, #f1f5f9 25%, transparent 25%, transparent 75%, #f1f5f9 75%), linear-gradient(45deg, #f1f5f9 25%, transparent 25%, transparent 75%, #f1f5f9 75%)" : undefined, backgroundSize: !c ? "8px 8px" : undefined, backgroundPosition: !c ? "0 0, 4px 4px" : undefined }}
                        title={c || "Rimuovi colore"}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={onEdit}
              className="p-1 rounded hover:bg-white/60 text-slate-600 hover:text-slate-800 transition-colors"
              title="Rinomina"
              data-testid={`edit-stage-${stage.id}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={onDelete}
              className="p-1 rounded hover:bg-white/60 text-slate-600 hover:text-rose-600 transition-colors"
              title="Cancella"
              data-testid={`delete-stage-${stage.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between text-[11px] text-slate-800">
          <span className="font-semibold">{opps.length} piani</span>
          <span className="font-bold">{fmtEuro(totalValue)}</span>
        </div>
      </div>

      <div className="space-y-2.5 flex-1 overflow-y-auto min-h-0 scrollbar-hide pr-1 -mr-1">
        {loading ? (
          <div className="text-center py-8 text-slate-300">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" />
          </div>
        ) : opps.length === 0 ? (
          <div className="text-center py-8 text-slate-300 text-xs italic">Vuoto</div>
        ) : (
          opps.map((o) => (
            <OppCard
              key={o.id}
              opp={o}
              onDragStart={(e) => onDragStart(o, e)}
              onNameClick={() => onOppClick(o)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function OppCard({ opp, onDragStart, onNameClick }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      className="bg-white rounded-xl border border-slate-200 p-4 cursor-grab active:cursor-grabbing hover:shadow-md hover:border-indigo-300 transition-all group space-y-2.5"
      data-testid={`opp-card-${opp.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNameClick(); }}
          className="text-sm font-semibold text-slate-900 capitalize truncate flex-1 text-left hover:text-indigo-600 hover:underline transition-colors"
          data-testid={`opp-name-${opp.id}`}
        >
          {opp.contactName || opp.oppName || <span className="italic text-slate-400 normal-case">No contatto</span>}
        </button>
        <GripVertical className="w-4 h-4 text-slate-300 group-hover:text-slate-500 flex-shrink-0 mt-0.5" />
      </div>

      {opp.source && (
        <div className="flex items-center gap-1.5 text-xs text-slate-600">
          <span className="font-medium text-slate-500">Assicurazione:</span>
          <span className="truncate">{opp.source}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
        <span className="text-sm font-bold text-slate-800">{fmtEuro(opp.monetaryValue)}</span>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[opp.status] || STATUS_COLORS.open}`}>
          {opp.status || "open"}
        </span>
      </div>

      <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
        <Clock className="w-3 h-3" />
        <span>Entrata in fase: {fmtDate(opp.enteredAt)}</span>
      </div>
    </div>
  );
}


// ─── Dialogs ────────────────────────────────────

function PipelineDialog({ state, onClose, onSuccess }) {
  const { open, mode, pipeline } = state;
  const [name, setName] = useState("");
  const [stages, setStages] = useState("Nuovo");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(pipeline?.name || "");
      setStages("Nuovo, In corso, Completato");
    }
  }, [open, pipeline]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Nome pipeline obbligatorio"); return; }
    setSaving(true);
    try {
      if (mode === "create") {
        const stagesArr = stages.split(",").map((s) => s.trim()).filter(Boolean);
        await axios.post(`${API}/piani-cura/pipelines`, { name: trimmed, stages: stagesArr }, { withCredentials: true });
        toast.success("Pipeline creata");
      } else if (mode === "edit") {
        await axios.put(`${API}/piani-cura/pipelines/${pipeline.id}`, { name: trimmed }, { withCredentials: true });
        toast.success("Pipeline rinominata");
      } else if (mode === "delete") {
        await axios.delete(`${API}/piani-cura/pipelines/${pipeline.id}`, { withCredentials: true });
        toast.success("Pipeline cancellata");
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  const title = mode === "create" ? "Nuova pipeline" : mode === "edit" ? "Rinomina pipeline" : `Cancella "${pipeline?.name}"`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pipeline-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {mode === "delete" ? (
          <div className="space-y-3">
            <div className="flex items-start gap-3 bg-rose-50 border border-rose-100 rounded-lg p-3">
              <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-rose-700">
                Attenzione: la pipeline verrà eliminata da GoHighLevel. Devi prima spostare o archiviare tutte le opportunità attive.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Nome pipeline</label>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                data-testid="pipeline-name-input"
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
                autoFocus
              />
            </div>
            {mode === "create" && (
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Fasi iniziali (separate da virgola)</label>
                <input
                  type="text" value={stages} onChange={(e) => setStages(e.target.value)}
                  data-testid="pipeline-stages-input"
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
                />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="pipeline-confirm-btn"
            className={mode === "delete" ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-indigo-600 hover:bg-indigo-700 text-white"}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "delete" ? "Cancella" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StageDialog({ state, onClose, onSuccess }) {
  const { open, mode, pipelineId, stage } = state;
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setName(stage?.name || ""); }, [open, stage]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (mode === "create") {
        const trimmed = name.trim();
        if (!trimmed) { toast.error("Nome obbligatorio"); setSaving(false); return; }
        await axios.post(`${API}/piani-cura/pipelines/${pipelineId}/stages`, { name: trimmed }, { withCredentials: true });
        toast.success("Fase aggiunta");
      } else if (mode === "edit") {
        const trimmed = name.trim();
        if (!trimmed) { toast.error("Nome obbligatorio"); setSaving(false); return; }
        await axios.put(`${API}/piani-cura/pipelines/${pipelineId}/stages/${stage.id}`, { name: trimmed }, { withCredentials: true });
        toast.success("Fase rinominata");
      } else if (mode === "delete") {
        await axios.delete(`${API}/piani-cura/pipelines/${pipelineId}/stages/${stage.id}`, { withCredentials: true });
        toast.success("Fase cancellata");
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  const title = mode === "create" ? "Nuova fase" : mode === "edit" ? "Rinomina fase" : `Cancella "${stage?.name}"`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="stage-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {mode === "delete" ? (
          <div className="flex items-start gap-3 bg-rose-50 border border-rose-100 rounded-lg p-3">
            <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-rose-700">La fase deve essere vuota. Sposta o archivia le opp prima di cancellare.</p>
          </div>
        ) : (
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            data-testid="stage-name-input"
            className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
            autoFocus placeholder="Nome della fase"
          />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button
            onClick={handleSave} disabled={saving}
            data-testid="stage-confirm-btn"
            className={mode === "delete" ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-indigo-600 hover:bg-indigo-700 text-white"}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "delete" ? "Cancella" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OppDialog({ state, onClose, onSuccess }) {
  const { open, opp } = state;
  const [monetary, setMonetary] = useState("");
  const [status, setStatus] = useState("open");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (open && opp) {
      setMonetary(String(opp.monetaryValue || 0));
      setStatus(opp.status || "open");
      setConfirmDelete(false);
    }
  }, [open, opp]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/piani-cura/opportunities/${opp.id}`, {
        monetaryValue: parseFloat(monetary) || 0,
        status,
      }, { withCredentials: true });
      toast.success("Opportunità aggiornata");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await axios.delete(`${API}/piani-cura/opportunities/${opp.id}`, { withCredentials: true });
      const expires = new Date(res.data.expires_at).toLocaleDateString("it-IT");
      toast.success(`Opportunità archiviata (ripristinabile fino al ${expires})`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  if (!open || !opp) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="opp-dialog">
        <DialogHeader>
          <DialogTitle className="capitalize">{opp.contactName || "Opportunità"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Valore (€)</label>
            <input
              type="number" step="0.01" value={monetary} onChange={(e) => setMonetary(e.target.value)}
              data-testid="opp-monetary-input"
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Status</label>
            <select
              value={status} onChange={(e) => setStatus(e.target.value)}
              data-testid="opp-status-select"
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
            >
              <option value="open">Aperta</option>
              <option value="won">Vinta</option>
              <option value="lost">Persa</option>
              <option value="abandoned">Abbandonata</option>
            </select>
          </div>

          {confirmDelete && (
            <div className="flex items-start gap-3 bg-rose-50 border border-rose-100 rounded-lg p-3">
              <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-rose-700">
                L&apos;opp verrà cancellata da GHL. Sarà ripristinabile dall&apos;archivio per 30 giorni.
              </p>
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          {!confirmDelete && (
            <Button
              variant="outline" onClick={() => setConfirmDelete(true)}
              data-testid="opp-delete-btn"
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Elimina
            </Button>
          )}
          {confirmDelete && (
            <Button
              onClick={handleDelete} disabled={saving}
              data-testid="opp-confirm-delete-btn"
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sì, archivia"}
            </Button>
          )}
          <Button
            onClick={handleSave} disabled={saving}
            data-testid="opp-save-btn"
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Salva</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function OppDetailDialog({ state, user, onClose }) {
  const { open, oppId } = state;
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !oppId) return;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/piani-cura/opportunities/${oppId}/detail`, { withCredentials: true });
        setDetail(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Errore caricamento dettagli");
      } finally { setLoading(false); }
    })();
  }, [open, oppId]);

  if (!open) return null;
  const cur = detail?.current || {};
  const c = detail?.contact || {};
  const piano = detail?.piano || {};
  const hasPiano = Boolean(piano.opportunity_id);
  const groups = piano.piano_cura || [];
  const foto = piano.foto_urls || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="opp-detail-dialog">
        <DialogHeader>
          <DialogTitle className="capitalize">
            {c.contactName || cur.opp_name || "Piano di Cura"}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div>
        ) : !detail ? (
          <div className="text-center py-12 text-slate-400">Nessun dettaglio</div>
        ) : (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Contatto */}
            <section className="bg-slate-50 rounded-xl p-4 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Contatto</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <DetailField icon={User} label="Nome" value={c.contactName || `${c.firstName || ""} ${c.lastName || ""}`.trim()} className="capitalize" />
                <DetailField icon={Calendar} label="Data nascita" value={c.dateOfBirth ? fmtDate(c.dateOfBirth) : "—"} />
                <DetailField icon={Phone} label="Telefono" value={c.phone || piano.telefono || "—"} />
                <DetailField icon={Mail} label="Email" value={c.email || piano.email || "—"} className="lowercase truncate" />
              </div>
            </section>

            {/* Piano di cura — dettagli principali */}
            <section className="bg-slate-50 rounded-xl p-4 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Piano di Cura</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                <DetailField icon={FolderOpen} label="Fase attuale" value={cur.stage_name || "—"} />
                <DetailField label="Assicurazione" value={piano.assicurazione_name || cur.source || "—"} />
                <DetailField icon={Stethoscope} label="Medico" value={piano.medico_name || "—"} className="capitalize" />
                <DetailField icon={UserCog} label="Assistente" value={piano.assistente_name || "—"} className="capitalize" />
                <DetailField icon={Calendar} label="Data piano" value={piano.data_piano_cura ? fmtDate(piano.data_piano_cura) : fmtDate(cur.data_inserimento)} />
                <DetailField icon={Clock} label="Entrata in fase" value={fmtDate(cur.entered_at)} />
                <DetailField label="Data chiusura prevista" value={fmtDate(cur.data_chiusura_prevista)} />
                <DetailField label="Status" value={cur.status || "open"} className="capitalize" />
                <DetailField label="Casi particolari" value={cur.casi_particolari || "—"} />
              </div>
            </section>

            {/* Prestazioni (piano_cura groups) */}
            {hasPiano && groups.length > 0 && (
              <section className="bg-slate-50 rounded-xl p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">
                  Prestazioni ({groups.reduce((n, g) => n + (g.trattamenti || []).length, 0)})
                </p>
                <div className="space-y-3">
                  {groups.map((g, gi) => {
                    const denti = g.fullMouth ? "Arcata completa" : (g.denti || []).join(", ") || "—";
                    return (
                      <div key={g.id || gi} className="bg-white rounded-lg border border-slate-200 p-3">
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-600 mb-2">
                          <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded">Denti</span>
                          <span className="text-slate-700">{denti}</span>
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-slate-400 border-b border-slate-100">
                              <th className="pb-1 font-semibold">Codice</th>
                              <th className="pb-1 font-semibold">Prestazione</th>
                              <th className="pb-1 font-semibold text-right">Prezzo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(g.trattamenti || []).map((t, ti) => (
                              <tr key={ti} className="border-b border-slate-50 last:border-0">
                                <td className="py-1 font-mono text-slate-500">{t.treatmentCode || "—"}</td>
                                <td className="py-1 text-slate-800">{t.treatmentName || "—"}</td>
                                <td className="py-1 text-right font-semibold text-slate-800">{fmtEuro(t.euro)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Totali */}
            <section className="bg-slate-50 rounded-xl p-4 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
              <DetailField icon={Euro} label="Valore opp (GHL)" value={fmtEuro(cur.monetary_value)} />
              {hasPiano && (
                <DetailField label="Totale piano" value={fmtEuro(piano.total_cost)} />
              )}
              {hasPiano && piano.total_cost_base ? (
                <DetailField label="Totale BDD" value={fmtEuro(piano.total_cost_base)} />
              ) : null}
            </section>

            {/* Foto (thumbnails) */}
            {foto.length > 0 && (
              <section className="bg-slate-50 rounded-xl p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
                  <Camera className="w-3.5 h-3.5" /> Foto RX ({foto.length})
                </p>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {foto.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block aspect-square bg-white rounded-lg overflow-hidden border border-slate-200 hover:shadow-md hover:border-indigo-300 transition-all">
                      <img src={url} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    </a>
                  ))}
                </div>
              </section>
            )}

            {/* Metadata */}
            {hasPiano && (
              <section className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span>Creato il <b className="text-slate-700">{fmtDate(piano.created_at)}</b> da <b className="text-slate-700">{piano.created_by_email || "—"}</b></span>
                {piano.last_updated_at && piano.last_updated_at !== piano.created_at && (
                  <span>· Aggiornato il <b className="text-slate-700">{fmtDate(piano.last_updated_at)}</b> da <b className="text-slate-700">{piano.last_updated_by_email || "—"}</b></span>
                )}
                {piano.revision_count > 1 && (
                  <span>· Revisione <b className="text-slate-700">{piano.revision_count}</b></span>
                )}
              </section>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="detail-close-btn">Chiudi</Button>
          {detail && (() => {
            // Determina permessi utente sullo stage attuale e apri
            // la pagina originale del piano di cura (in modalità edit
            // oppure consult=readonly) invece del dialog quick-edit.
            const pipelineId = cur.pipeline_id;
            const stageId = cur.stage_id;
            const mode = PIPELINE_TO_MODE[pipelineId] || "clinico";
            const perm = getStagePermission(user, pipelineId, stageId);
            const canEdit = perm.edit;
            const canVision = perm.vision || perm.edit;
            if (!canVision) {
              return (
                <Button
                  variant="outline"
                  disabled
                  data-testid="detail-locked-btn"
                  className="text-slate-400 border-slate-200 cursor-not-allowed"
                >
                  <Lock className="w-4 h-4 mr-1.5" /> Nessun accesso
                </Button>
              );
            }
            const params = new URLSearchParams({ opportunityId: oppId });
            if (!canEdit) params.set("consult", "1");
            const targetUrl = `/piano-cura-${mode}?${params.toString()}`;
            return (
              <Button
                onClick={() => {
                  // Marker letto da Navbar/App.js/OpportunityFormBanners
                  // per riportare l'utente al Kanban dopo save/exit.
                  try { sessionStorage.setItem("pianiCuraReturnActive", "1"); } catch (_) { /* privacy mode */ }
                  // Clear altri marker per evitare conflitti (arrivo da
                  // Kanban, non da Archivio/Chiama/Promemoria).
                  try {
                    sessionStorage.removeItem("archiveReturnActive");
                    sessionStorage.removeItem("chiamaReturnActive");
                    sessionStorage.removeItem("promemoriaReturnActive");
                  } catch (_) { /* privacy mode */ }
                  onClose();
                  navigate(targetUrl);
                }}
                data-testid={canEdit ? "detail-edit-btn" : "detail-view-btn"}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {canEdit ? (<><Pencil className="w-4 h-4 mr-1.5" /> Modifica</>) : (<><Eye className="w-4 h-4 mr-1.5" /> Consulta</>)}
              </Button>
            );
          })()}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailField({ icon: Icon, label, value, className = "" }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </p>
      <p className={`text-sm text-slate-800 ${className}`}>{value || "—"}</p>
    </div>
  );
}


function ArchiveDialog({ state, onClose, onRestored }) {
  const { open } = state;
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ttlDays, setTtlDays] = useState(30);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/piani-cura/archived`, { withCredentials: true });
        setArchives(res.data.archives || []);
        setTtlDays(res.data.ttl_days || 30);
      } catch (err) {
        toast.error("Errore caricamento archivio");
      } finally { setLoading(false); }
    })();
  }, [open]);

  const restore = async (aid) => {
    try {
      await axios.post(`${API}/piani-cura/archived/${aid}/restore`, {}, { withCredentials: true });
      toast.success("Opportunità ripristinata");
      setArchives((prev) => prev.filter((a) => a.archive_id !== aid));
      onRestored();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore ripristino");
    }
  };

  const purge = async (aid) => {
    if (!window.confirm("Eliminare definitivamente questo snapshot? Non sarà più ripristinabile.")) return;
    try {
      await axios.delete(`${API}/piani-cura/archived/${aid}`, { withCredentials: true });
      setArchives((prev) => prev.filter((a) => a.archive_id !== aid));
      toast.success("Snapshot eliminato");
    } catch (err) {
      toast.error("Errore eliminazione");
    }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="archive-dialog">
        <DialogHeader>
          <DialogTitle>Archivio opportunità</DialogTitle>
          <p className="text-xs text-slate-500">Ripristinabili entro {ttlDays} giorni dalla cancellazione</p>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div>
          ) : archives.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Archive className="w-10 h-10 mx-auto opacity-40" />
              <p className="mt-3 text-sm">Nessun archivio</p>
            </div>
          ) : (
            <div className="space-y-2">
              {archives.map((a) => {
                const snap = a.snapshot || {};
                const archived = new Date(a.archived_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
                return (
                  <div key={a.archive_id} className="border border-slate-200 rounded-lg p-3 flex items-center gap-3">
                    <Archive className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">
                        {snap.name || snap.stage_name || snap.opp_id || "Opportunità"}
                      </p>
                      <p className="text-xs text-slate-500">
                        Archiviata {archived} da {a.archived_by || "—"}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => restore(a.archive_id)} data-testid={`archive-restore-${a.archive_id}`}>
                      <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" /> Ripristina
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => purge(a.archive_id)} data-testid={`archive-purge-${a.archive_id}`} className="text-slate-400 hover:text-rose-600">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



